import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Container, Typography, TextField, Button, Box, Alert ,MenuItem,
  InputAdornment,
  IconButton
} from '@mui/material';
import api from '../services/api';
import PhoneNumberField from '../components/PhoneNumberField';
import {   
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';

const RegisterPage = ({keywordsData, language}) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    age: '',
    sex: '',
    user_phone: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Handle change for regular text fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Validate field if it's been touched
    if (touched[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: validateField(name, value)
      }));
    }
  };

  // Handle phone number change (from custom component)
  const handlePhoneChange = (value) => {
    setFormData(prev => ({
      ...prev,
      user_phone: value
    }));
    
    if (touched.user_phone) {
      setErrors(prev => ({
        ...prev,
        user_phone: validatePhone(value)
      }));
    }
  };

  // Field validation
  const validateField = (field, value) => {
    switch (field) {
      case 'username':
        return validateUsername(value);
      case 'email':
        return validateEmail(value);
      case 'password':
        return validatePassword(value);
      case 'confirmPassword':
        return validateConfirmPassword(value);
      case 'user_phone':
        return validatePhone(value);
      case 'age':
        return validateAge(value);
      case 'sex':
        return validateSex(value);
      default:
        return '';
    }
  };

  const validateAge = (age) => {
    if (!age) return language === 'ar' ? 'العمر مطلوب' : 'Age required ';
    if (age && (age < 1 || age > 120)) return language === 'ar' ? 'الرجاء إدخال عمر صالح' : 'Please enter valid age';
    return '';
  };

  const validateSex = (sex) => {
    if (sex === "") return language === 'ar' ? 'نوع الجنس مطلوب' : 'Sex required';
    return '';
  };

  const validateUsername = (username) => {
    if (!username) return language === 'ar' ? 'إسم المستخدم مطلوب' : 'UserName required';
    if (username.length < 3) return language === 'ar' ? 'اسم المستخدم يجب أن يتكون من 3 أحرف على الأقل' : 'The username must be at least 3 characters long.';
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      return language === 'ar' ? 'يجب أن يحتوي اسم المستخدم فقط على أحرف وأرقام وشرطات سفلية' : 'The username must contain only letters, numbers, and underscores.';
    }
    return '';
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return language === 'ar' ? 'الإيميل مطلوب' : 'E-mail required';
    if (!regex.test(email)) return language === 'ar' ? 'يرجى إدخال عنوان بريد إلكتروني صالح' : 'Please enter a valid email address';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return language === 'ar' ? 'كلمة المرور مطلوبة' : 'Password required';
    if (password.length < 6) return language === 'ar' ?  'يجب أن تتكون كلمة المرور من 6 أحرف على الأقل' : 'The password must be at least 6 characters long.';
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      return language === 'ar' ? 'يجب أن تحتوي كلمة المرور على أحرف كبيرة وصغيرة وأرقام' : 'The password must contain uppercase and lowercase letters and numbers.';
    }
    return '';
  };

  const validateConfirmPassword = (confirmPassword) => {
    if (!confirmPassword) return language === 'ar' ? 'الرجاء تأكيد كلمة المرور الخاصة بك' : 'Please confirm your password';
    if (confirmPassword !== formData.password) return language === 'ar' ? 'كلمة المرور غير متطابقة' : 'The password does not match';
    return '';
  };

  const validatePhone = (phone) => {
    const regex = /^(\+60|0)?1[0-9]{9}$/;
    if (!phone) return language === 'ar' ? 'رقم الهاتف مطلوب' : 'Phone Number required';
    if (!regex.test(phone.replace(/\s/g, ''))) {
      return language === 'ar' ? 'الرجاء إدخال رقم هاتف ماليزي صحيح (على سبيل المثال، 01234567890 أو +601234567890)' : 'Please enter a valid Malysian phone number (for example, 01234567890 or +601234567890)';
    }
    return '';
  };

  // Handle blur event
  const handleBlur = (field) => (e) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prev => ({
      ...prev,
      [field]: validateField(field, formData[field])
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newTouched = {};
    const newErrors = {};
    
    Object.keys(formData).forEach(field => {
      newTouched[field] = true;
      newErrors[field] = validateField(field, formData[field]);
    });
    
    setTouched(newTouched);
    setErrors(newErrors);
    
    // Check if there are any errors
    if (Object.values(newErrors).some(error => error)) {
      return;
    }
    
    try {
      await api.post('/auth/register', {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        user_phone: formData.user_phone,
        age: formData.age,
        sex: formData.sex
      });
      
      navigate('/login', { 
        state: { 
          message: language === 'ar' ? 'تم التسجيل بنجاح, الرجاء تسجيل الدخول' : 'Registration successful, please login'
        } 
      });
    } catch (err) {
      setErrors({ 
        submit: err.response?.data?.message || language === 'ar' ? 'فشل التسجيل' : 'Registration feild'
      });
    }
  };

  // Check if form is valid
  const isFormValid = () => {
    return Object.values(errors).every(error => !error) && 
           Object.keys(touched).length === Object.keys(formData).length;
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4, mb: 10 }}>
      <Typography variant="h4" gutterBottom align="center">
        {language === 'ar' ? 'إنشاء حساب' : 'Registration'}
      </Typography>
      
      {errors.submit && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errors.submit}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
        <TextField
          fullWidth
          margin="normal"
          label={language === 'ar' ? 'إسم المستخدم' : 'User Name'}
          name="username"
          value={formData.username}
          onChange={handleChange}
          onBlur={handleBlur('username')}
          error={touched.username && !!errors.username}
          helperText={touched.username && errors.username}
          required
        />
        
        <TextField
          fullWidth
          margin="normal"
          label={language === 'ar' ? 'الإيميل' : 'E-mail'}
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          onBlur={handleBlur('email')}
          error={touched.email && !!errors.email}
          helperText={touched.email && errors.email}
          required
        />
        
       <TextField
          fullWidth
          margin="normal"
          label={language === 'ar' ? 'العمر' : 'Age'}
          type="number"
          name="age"
          value={formData.age}
          onChange={handleChange}
          onBlur={handleBlur('age')}
          error={touched.age && !!errors.age}
          helperText={touched.age && errors.age}
          inputProps={{ min: 1, max: 120 }}
          required
        />
        
        <TextField
          select
          fullWidth
          margin="normal"
          label={language === 'ar' ? 'الجنس' : 'Sex'}
          name="sex"
          value={formData.sex}
          onChange={handleChange}
          onBlur={handleBlur('sex')}
          error={touched.sex && !!errors.sex}
          helperText={touched.sex && errors.sex}
          required
        >
          <MenuItem value="">{language === 'ar' ? 'إختر نوع الجنس' : 'Choose sex type'}</MenuItem>
          <MenuItem value="male">{language === 'ar' ? 'ذكر' : 'Male'}</MenuItem>
          <MenuItem value="female">{language === 'ar' ? 'انثى' : 'Female'}</MenuItem>
        </TextField>

        <PhoneNumberField
          fullWidth
          margin="normal"
          label={language === 'ar' ? 'رقم الهاتف' : 'Phone Number'}
          name="user_phone"
          value={formData.user_phone}
          onChange={handlePhoneChange}
          onBlur={handleBlur('user_phone')}
          error={touched.user_phone && !!errors.user_phone}
          helperText={touched.user_phone && errors.user_phone}
          lang={language}
          required
        />
        
        <TextField
          fullWidth
          margin="normal"
          label={language === 'ar' ? 'كلمة المرور' : 'Password'}
          type={showPassword ? 'text' : 'password'}
          name="password"
          value={formData.password}
          onChange={handleChange}
          onBlur={handleBlur('password')}
          error={touched.password && !!errors.password}
          helperText={touched.password && errors.password}
          required
          InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#B3B3B3' }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
              }}
        />
        
        <TextField
          fullWidth
          margin="normal"
          label={language === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'}
          type={showPassword ? 'text' : 'password'}
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          onBlur={handleBlur('confirmPassword')}
          error={touched.confirmPassword && !!errors.confirmPassword}
          helperText={touched.confirmPassword && errors.confirmPassword}
          required
          InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#B3B3B3' }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
              }}
        />
        
        <Button 
          type="submit" 
          variant="contained" 
          fullWidth 
          sx={{ mt: 3, py: 1.5 }}
          disabled={!isFormValid()}
        >
          {language === 'ar' ? 'إنشاء الحساب' : 'Creat account'}
        </Button>
      </Box>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body1">
          {language === 'ar' ? 'هل لديك حساب بالفعل ؟' : 'Already have an account?'} {' '}
          <Button 
            component={Link} 
            to="/login" 
            color="warning"
            sx={{ textTransform: 'none' }}
          >
            {language === 'ar' ? 'سجل دخولك من هنا' : 'Login here'}
          </Button>
        </Typography>
      </Box>
    </Container>
  );
};

export default RegisterPage;